import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GlobalService } from '../global.service';

export interface CronStatus {
  _id: string;
  name: string;
  timestamp: number;
  usageInterval?: number | null;
}

@Injectable()
export class ControleCronService {
  private readonly lateThresholdMs = 3 * 60 * 1000;

  private cronStatusesSubject = new BehaviorSubject<CronStatus[]>([]);
  cronStatuses$ = this.cronStatusesSubject.asObservable();

  private hasLateCronSubject = new BehaviorSubject<boolean>(false);
  hasLateCron$ = this.hasLateCronSubject.asObservable();

  private lateCronCountSubject = new BehaviorSubject<number>(0);
  lateCronCount$ = this.lateCronCountSubject.asObservable();

  private autoRefreshHandle?: ReturnType<typeof setInterval>;

  constructor(private globalService: GlobalService) {}

  refreshCache(): Observable<CronStatus[]> {
    return this.globalService.get('/controle-cron/').pipe(
      map((response: any) => {
        const payload = Array.isArray(response?.data) ? response.data : [];
        const normalized: CronStatus[] = payload.map((item: any) => ({
          _id: item?._id,
          name: item?.name ?? 'Inconnu',
          timestamp: item?.timestamp ?? 0,
          usageInterval: item?.usageInterval ?? null
        }));

        this.updateSubjects(normalized);

        return normalized;
      })
    );
  }

  startAutoRefresh(intervalMs = 30000): void {
    if (this.autoRefreshHandle) {
      return;
    }

    this.refreshCache().subscribe({
      error: (error) => console.warn('⚠️ Impossible d’actualiser les cron au démarrage:', error)
    });

    this.autoRefreshHandle = setInterval(() => {
      this.refreshCache().subscribe({
        error: (error) => console.warn('⚠️ Impossible d’actualiser les cron:', error)
      });
    }, intervalMs);
  }

  stopAutoRefresh(): void {
    if (this.autoRefreshHandle) {
      clearInterval(this.autoRefreshHandle);
      this.autoRefreshHandle = undefined;
    }
  }

  getSnapshot(): CronStatus[] {
    return this.cronStatusesSubject.getValue();
  }

  isCronLate(cron: CronStatus): boolean {
    return (Date.now() - (cron.timestamp || 0)) > this.lateThresholdMs;
  }

  getTimeSinceUpdate(cron: CronStatus): number {
    return Math.max(0, Date.now() - (cron.timestamp || 0));
  }

  add(data: { name: string; timestamp: number; usageInterval: number }): Observable<any> {
    return this.globalService.post('/controle-cron/', data);
  }

  private updateSubjects(data: CronStatus[]): void {
    this.cronStatusesSubject.next(data);
    const staleCount = data.filter(cron => this.isCronLate(cron)).length;
    this.hasLateCronSubject.next(staleCount > 0);
    this.lateCronCountSubject.next(staleCount);
  }
}

